import { FileMarkdownTwoTone, FileTwoTone, InboxOutlined, FileTextOutlined } from "@ant-design/icons";
import { Create, getValueFromEvent, useFileUploadState, useForm } from "@refinedev/antd";

import { Form, Tag, Upload, UploadProps, Space } from "antd";
import { IResourceComponentsProps, useApiUrl, useNotification } from "@refinedev/core";
import axios from "axios";
import { IInvoiceDataReport } from "interfaces";
import { colors } from "config/theme";
import { FormSection } from "components/form";

import React from "react";

export const InvoiceDataCreate: React.FC<IResourceComponentsProps> = () => {
  const [fileList, setFileList] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [isFileUploaded, setIsFileUploaded] = React.useState(false);

  const { open, close } = useNotification();
  const {
    formProps,
    saveButtonProps,
    formLoading: isLoading,
  } = useForm<IInvoiceDataReport>({ redirect: "list" });

  const { onChange, isLoading: isFileUploading } = useFileUploadState();
  const apiURL = useApiUrl();

  const handleIconRender: UploadProps["iconRender"] = (file, listType) => {
    return file.name.endsWith(".msg") ? (
      <FileMarkdownTwoTone twoToneColor={colors.success} />
    ) : (
      <FileTwoTone twoToneColor={colors.error} />
    );
  };

  const beforeUpload = (file: any) => {
    if (!file.name.endsWith(".msg")) {
      open?.({
        type: "error",
        message: "Error",
        description: `${file.name} is not a valid file type`,
      });
      close?.("error-notification-key");
      return true;
    }
    return false;
  };

  const handleChange = (data: any) => {
    const { fileList } = data;
    setFileList(
      fileList.filter((file: { status: string }) => file.status !== "error")
    );

    setIsFileUploaded(true);
    onChange(data);
  };

  const onRemove = async (file: any) => {
    const index = fileList.indexOf(file as never);
    const newFileList = fileList.slice();
    newFileList.splice(index, 1);

    setFileList(newFileList);
  };

  const getBase64 = (file: any) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFinish = async (values: any) => {
    setSubmitting(true);

    await uploadFiles();

    await new Promise((resolve) => {
      return setTimeout(resolve, 5000);
    });
    setSubmitting(false);
  };

  const uploadFiles = async () => {
    const uploadPromises = Array.from(fileList).map(async (file: any) => {
      const { data: signedUrl } = await axios.get(
        `${apiURL}/ukho-invoice/storage`,
        {
          withCredentials: false,
          headers: {
            "Access-Control-Allow-Origin": "*",
          },
        }
      );

      await axios.put(
        signedUrl,
        file,
        {
          withCredentials: false,
          headers: {
            "Access-Control-Allow-Origin": "*",
            "Content-Type": file.type,
          },
        }
      );
    });

    await Promise.all(uploadPromises)
      .then(
        axios.spread((data1, data2) => {
          open?.({
            type: "success",
            message: `Updated application wide settings`,
            description: "Success",
          });
        })
      )
      .catch(
        axios.spread((data1, data2) => {
          open?.({
            type: "error",
            message: "Failed to update application wide settings",
            description: "Error",
          });
        })
      );
  };

  const props = {
    accept: ".msg",
    name: "file",
    multiple: true,
    fileList: fileList,
    onChange: handleChange,
    onRemove: onRemove,
    beforeUpload: beforeUpload,
    iconRender: handleIconRender,
  };

  return (
    <Create
      breadcrumb={false}
      saveButtonProps={{
        ...saveButtonProps,
        disabled: isFileUploaded && !isFileUploading ? false : true,
      }}
      headerProps={{
        onBack: () => window.history.back(),
        title: "Report",
        subTitle: "invoice data",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
      }}
    >
      <Form {...formProps} onFinish={handleFinish} layout="vertical">
        <Space direction="vertical" size={24} style={{ width: "100%" }}>
          <FormSection
            title="File Upload"
            subtitle="Upload invoice data files (.msg format)"
            icon={<FileTextOutlined />}
            variant="elevated"
          >
            <Form.Item
              label="File"
              name="file"
              valuePropName="file"
              getValueFromEvent={getValueFromEvent}
            >
              <Upload.Dragger {...props} listType="picture">
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag & drop file to this area to upload
                </p>
                <p className="ant-upload-hint">
                  Support for multiple invoice file uploads
                </p>
              </Upload.Dragger>
            </Form.Item>
          </FormSection>
        </Space>
      </Form>
    </Create>
  );
};
